import { QueryKey } from '@tanstack/react-query'
import { getDeviceCommonName } from '@weenat/client/dist/core/devices'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import { HorizonOpt } from '@weenat/client/dist/core/horizons'
import { useMetricProvider } from '@weenat/client/dist/core/metrics'
import { useToggleIsFavorite } from '@weenat/client/dist/core/plots'
import {
  computeHasNoDataForHorizon,
  computeHasNotEmitted,
  getMetricLastUpdateFromSummary
} from '@weenat/client/dist/core/summary'
import { UniverseMetric } from '@weenat/client/dist/core/universes'
import {
  isFertigationMetric,
  isFrost,
  isSoilSensorMetric
} from '@weenat/client/dist/enums/MetricIds'
import {
  DeviceSummary,
  PlotSummary,
  StationSummary,
  isDeviceSummary,
  isPlotSummary,
  isStationSummary
} from '@weenat/client/dist/resources/measurements.type'
import { isArvalisPlotSummary } from '@weenat/client/dist/resources/waterBalanceArvalis'
import { useIntl } from '@weenat/wintl'
import { RestrictionReason, useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import useToasts from 'app/src/hooks/useToasts'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import NoEmissionEmptyState from 'app/src/kit/empty-state/NoEmissionEmptyState'
import Icons from 'app/src/kit/Icons'
import { FlexProps } from 'app/src/kit/primitives/themeMappings/props'
import Text from 'app/src/kit/Text'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { fromLngLatArrayToObject } from 'app/src/map/utils'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import { MouseEvent, ReactNode, memo, useCallback } from 'react'
import { styled, useTheme } from 'styled-components'
import { useBackgroundMapContext } from './contexts/BackgroundMapContext'
import NoDataForHorizon from './NoDataForHorizon'
import { PlotCardFooter, StyledCard } from './PlotCard'
import PlotListItemMetricSummary from './plotListItem/PlotListItemMetricSummary'

// ─── Header ──────────────────────────────────────────────────────────────────

const Titles = styled(Flex)`
  flex: 1;
  align-items: center;
  gap: 4px;
  overflow: hidden;
`

interface SummaryCardHeaderProps extends FlexProps {
  name: string
  reverseGeocoding: string | null
  Icon: ReactNode
  FavoriteSlot?: ReactNode
  onFocusPress?: () => void
}

const DumbSummaryCardHeader: FC<SummaryCardHeaderProps> = ({
  name,
  reverseGeocoding,
  Icon,
  FavoriteSlot,
  onFocusPress,
  ref: _notUsed,
  ...flexProps
}) => {
  const { t } = useIntl()
  const { colors } = useTheme()
  const [{ marker_redesign }] = useToggleFeature()

  return (
    <DelimitedFlex $alignItems='center' $p='md' $gap='md' {...flexProps}>
      {marker_redesign ? null : Icon}
      <Titles>
        <Flex $flexDirection='column' $minWidth={0} $flex={1}>
          <Text $flexed $fontWeight='bold' $ellipsis>
            {name}
          </Text>
          <Text $fontSize='sm' $color={'grayscale.700'} $ellipsis>
            {!isNil(reverseGeocoding)
              ? reverseGeocoding
              : t('models.device.unknown_location', { capitalize: true })}
          </Text>
        </Flex>
      </Titles>
      {FavoriteSlot}
      {onFocusPress != null ? (
        <div>
          <Icons.Focus
            $backgroundColor={colors.grayscale.black}
            $color={colors.grayscale.white}
            $p='md'
            $size='lg'
            onPress={onFocusPress}
          />
        </div>
      ) : null}
    </DelimitedFlex>
  )
}

const SummaryCardHeader = memo(DumbSummaryCardHeader)

// ─── Favorite button ──────────────────────────────────────────────────────────

interface FavoriteButtonProps {
  summary: PlotSummary | StationSummary
  summariesQueryCacheKey: QueryKey
}

const DumbFavoriteButton: FC<FavoriteButtonProps> = ({ summary, summariesQueryCacheKey }) => {
  const { t } = useIntl()
  const { addSuccessToast } = useToasts()

  const name = isPlotSummary(summary) ? summary.metadata.plot.name : summary.metadata.station.name

  const { mutation: toggleIsFavorite, request: isFavoriteRequest } = useToggleIsFavorite({
    summary,
    summariesQueryCacheKey,
    onSuccess: (data) => {
      if (isPlotSummary(summary)) {
        addSuccessToast(
          t(
            data.isFavorite
              ? 'actions.add_plot_to_favorites_success'
              : 'actions.remove_plot_from_favorites_success',
            { plotName: name }
          )
        )
      } else {
        addSuccessToast(
          t(
            data.isFavorite
              ? 'actions.add_station_to_favorites_success'
              : 'actions.remove_station_from_favorites_success',
            { stationName: name }
          )
        )
      }
    }
  })

  const isFavorite = isPlotSummary(summary)
    ? summary.metadata.plot.isFavorite
    : summary.metadata.station.isFavorite

  const onFavoritePress = useCallback(
    async (e: MouseEvent<Element>) => {
      e.preventDefault()
      await toggleIsFavorite()
    },
    [toggleIsFavorite]
  )

  return (
    <TextTooltip
      restMs={1000}
      content={t(`actions.${!isFavorite ? 'add_to_favorite' : 'remove_from_favorite'}`)}
    >
      <Icons.Star
        $p='md'
        $size='lg'
        $isLoading={isFavoriteRequest.isPending}
        onPress={onFavoritePress}
        $color={isFavorite ? 'primary.500' : 'grayscale.300'}
      />
    </TextTooltip>
  )
}

const FavoriteButton = memo(DumbFavoriteButton)

// ─── Content ─────────────────────────────────────────────────────────────────

interface SummaryCardContentProps {
  summary: PlotSummary | StationSummary | DeviceSummary
  focusedMetric: NonNullable<UniverseMetric>
  focusedHorizon: number | null
  availableHorizonOptions: HorizonOpt[]
  onHorizonChange?: (newHorizon: number | null) => void
  isDiscrete?: boolean
  isRestricted: boolean
  reason: RestrictionReason
  /**
   * Whether the device is virtual or not
   * PRE smv this is coming from useMetricProvider
   * POST smv this is coming directly from the device model
   * @default false
   */
  isVirtualDevice: boolean
  providerName: string
  timezone: string
  hasNotEmitted: boolean
  hasNoDataForHorizon: boolean
  shouldConfigureArvalis: boolean
  shouldConfigureMetric: boolean
  arvalisWBComputing: boolean
  arvalisWBError: boolean
}

const SummaryCardContent: FC<SummaryCardContentProps> = ({
  summary,
  focusedMetric,
  focusedHorizon,
  availableHorizonOptions,
  onHorizonChange,
  isDiscrete = false,
  isRestricted,
  reason,
  isVirtualDevice,
  providerName,
  timezone,
  hasNotEmitted,
  hasNoDataForHorizon,
  shouldConfigureArvalis,
  shouldConfigureMetric,
  arvalisWBComputing,
  arvalisWBError
}) => {
  const { t } = useIntl()

  const isPlot = isPlotSummary(summary)
  const isStation = isStationSummary(summary)
  const isDevice = isDeviceSummary(summary)

  // isSoilSensorMetric: HPOT, U_CAPA and T_SOIL_BY_HORIZON are plot-specific — redirect on station/device
  if (
    isStation &&
    isSoilSensorMetric(focusedMetric) &&
    isIrrigationDevice(summary.metadata.station)
  ) {
    return (
      <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $flex={1} $p='lg'>
        <Text $textAlign='center' $color='grayscale.700'>
          {t('models.device.actions.view_sensor_data_on_plot', { capitalize: true })}
        </Text>
      </Flex>
    )
  }

  if (
    isDevice &&
    isSoilSensorMetric(focusedMetric) &&
    isIrrigationDevice(summary.metadata.device)
  ) {
    return (
      <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $flex={1} $p='lg'>
        <Text $textAlign='center' $color='grayscale.700'>
          {t('models.device.actions.view_sensor_data_on_plot', { capitalize: true })}
        </Text>
      </Flex>
    )
  }

  if (isDiscrete) {
    return (
      <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $flex={1} $p='lg'>
        <Text $textAlign='center' $color='grayscale.700'>
          {t(
            isPlot
              ? 'models.plot.misc.no_data_for_metric'
              : 'models.device.misc.no_data_for_metric',
            { capitalize: true }
          )}
        </Text>
      </Flex>
    )
  }

  if (hasNotEmitted) {
    if (focusedMetric === 'WATER_TANK_LEVEL' && isVirtualDevice) {
      return (
        <NoEmissionEmptyState
          IconName={'SingleVirtualSensorFilled'}
          metrics={[focusedMetric]}
          title={t('weedget.empty_state.virtual_water_tank.waiting_calculation.title')}
          description={t('weedget.empty_state.virtual_water_tank.waiting_calculation.description')}
        />
      )
    } else {
      return (
        <NoEmissionEmptyState
          IconName={isVirtualDevice ? 'SingleVirtualSensorFilled' : 'SingleSensorFilled'}
          metrics={[focusedMetric]}
          title={t('weedget.not_emitted_yet.wait_message')}
          description={t(
            isDevice
              ? 'weedget.not_emitted_yet.waiting_for_sensor'
              : 'weedget.not_emitted_yet.waiting_on_plot'
          )}
        />
      )
    }
  }

  if (shouldConfigureArvalis || arvalisWBComputing || arvalisWBError) {
    return (
      <Flex
        $flexDirection='column'
        $alignItems='center'
        $justifyContent='center'
        $flex={1}
        $px='md'
        $py='xs'
      >
        <Box $p='sm'>
          <Text $textAlign='center'>
            {t(
              shouldConfigureArvalis
                ? 'dss.waterBalance.arvalis_plot_creation.not_configure_summary'
                : arvalisWBComputing
                  ? 'dss.waterBalance.arvalis_plot_creation.computing'
                  : 'dss.waterBalance.arvalis_plot_creation.error_fetching'
            )}
          </Text>
        </Box>
      </Flex>
    )
  }

  if (shouldConfigureMetric) {
    return (
      <Flex
        $flexDirection='column'
        $alignItems='center'
        $justifyContent='center'
        $flex={1}
        $px='md'
        $py='md'
      >
        <Box $p='sm'>
          <Text $textAlign='center'>
            {t(
              isFertigationMetric(focusedMetric)
                ? 'models.plot.misc.setup_irrigation'
                : 'errors.unknown'
            )}
          </Text>
        </Box>
      </Flex>
    )
  }

  if (isSoilSensorMetric(focusedMetric) && hasNoDataForHorizon) {
    return (
      <NoDataForHorizon
        metric={focusedMetric}
        currentHorizon={focusedHorizon}
        onHorizonChange={onHorizonChange}
        availableHorizonOptions={availableHorizonOptions}
      />
    )
  }

  if (isStation || isDevice) {
    return (
      <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $flex={1} $p='md'>
        <PlotListItemMetricSummary
          metric={focusedMetric}
          data={summary.data}
          focusedHorizon={null}
          isRestricted={isRestricted}
          reason={reason}
          isVirtualDevice={isVirtualDevice}
          provider={providerName}
          timezone={timezone}
        />
      </Flex>
    )
  }

  return (
    <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $flex={1} $p='md'>
      <PlotListItemMetricSummary
        metric={focusedMetric}
        data={summary.data}
        focusedHorizon={focusedHorizon}
        isRestricted={isRestricted}
        reason={reason}
        isVirtualDevice={isVirtualDevice}
        provider={providerName}
        timezone={timezone}
      />
    </Flex>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────

interface SummaryCardFooterProps {
  shouldHide: boolean
  showConfigureLink: boolean
  shouldConfigureMetric: boolean
  timestamp: string | null | undefined
  timezone: string
  /** @deprecated */
  isVirtualMetric: boolean | undefined
  warningThreshold: number
  dangerThreshold: number
  providerName: string
}

const DumbSummaryCardFooter: FC<SummaryCardFooterProps> = ({
  shouldHide,
  showConfigureLink,
  shouldConfigureMetric,
  timestamp,
  timezone,
  isVirtualMetric,
  warningThreshold,
  dangerThreshold,
  providerName
}) => {
  const { t } = useIntl()
  const [{ marker_redesign }] = useToggleFeature()

  if (shouldHide) return null

  if (showConfigureLink) {
    return (
      <DelimitedFlex $isDelimitedOnTop $py={shouldConfigureMetric ? 'md' : 'lg'}>
        <Text $fontWeight='bold' style={{ margin: 'auto' }}>
          {t('actions.configure')}
        </Text>
      </DelimitedFlex>
    )
  }

  return (
    <PlotCardFooter
      timestamp={timestamp}
      timezone={timezone}
      isVirtualMetric={marker_redesign ? undefined : isVirtualMetric}
      warningThreshold={warningThreshold}
      dangerThreshold={dangerThreshold}
      name={providerName}
    />
  )
}

const SummaryCardFooter = memo(DumbSummaryCardFooter)
SummaryCardFooter.displayName = 'SummaryCardFooter'

// ─── Card ─────────────────────────────────────────────────────────────────────

interface SummaryCardProps {
  summary: PlotSummary | StationSummary | DeviceSummary
  focusedMetric: UniverseMetric
  focusedHorizon: number | null
  isSelected?: boolean
  isExpertOrg?: boolean
  isDiscrete?: boolean
  isVirtual?: boolean
  summariesQueryCacheKey?: QueryKey
  availableHorizonOptions?: HorizonOpt[]
  onHorizonChange?: (newHorizon: number | null) => void
  onPress: () => void
}

const SummaryCard: FC<SummaryCardProps> = ({
  summary,
  focusedMetric,
  focusedHorizon,
  isDiscrete = false,
  isSelected = false,
  isExpertOrg = false,
  isVirtual = false,
  summariesQueryCacheKey,
  availableHorizonOptions = [],
  onHorizonChange,
  onPress
}) => {
  const { t } = useIntl()
  const [{ marker_redesign, smv, on_nav_favorites }] = useToggleFeature()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  const isPlot = isPlotSummary(summary)
  const isStation = isStationSummary(summary)
  const isDevice = isDeviceSummary(summary)

  const resource = isPlot
    ? summary.metadata.plot
    : isStation
      ? summary.metadata.station
      : summary.metadata.device

  const { timezone, reverseGeocoding, location } = resource

  const name = isDevice
    ? `${getDeviceCommonName(summary.metadata.device)} (${summary.metadata.device.endUserName?.join(' ')})`
    : isPlot
      ? summary.metadata.plot.name
      : summary.metadata.station.name

  const focusOnMap = useCallback(() => {
    if (!isNil(location) && location.coordinates.length >= 2 && !isNil(map) && !isNil(maps)) {
      const latLng = fromLngLatArrayToObject(location.coordinates)
      map.fitBounds(new maps.LatLngBounds(latLng, latLng), 90)
      map.setZoom(16)
    }
  }, [location, map, maps])

  const {
    warningThreshold,
    lastUpdateThresholds,
    name: _providerName,
    deviceIds = []
  } = useMetricProvider({
    resource,
    metric: focusedMetric,
    isExpertOrg,
    smv
  })

  const isVirtualWaterTank =
    focusedMetric === 'WATER_TANK_LEVEL' &&
    isPlotSummary(summary) &&
    summary.metadata.plot.currentMeasurementConfigs
      .map((measurement) => measurement.metric)
      .includes('VIRTUAL_WATER_TANK_LEVEL')

  const isVirtualWaterTankThatHasNotEmittedYet =
    isVirtualWaterTank &&
    isNil(
      summary.metadata.plot.currentMeasurementConfigs.find(
        (measurement) => measurement.metric === 'VIRTUAL_WATER_TANK_LEVEL'
      )?.lastMeasuredAt
    )

  const providerName = isVirtualWaterTank ? t('dataSources.soil_vision') : _providerName

  const lastUpdate = getMetricLastUpdateFromSummary({
    summary,
    metric: focusedMetric,
    horizonId: isPlot && deviceIds.length <= 1 ? focusedHorizon : null
  })

  const hasNotEmitted =
    computeHasNotEmitted(summary, focusedMetric, isVirtual) ||
    isVirtualWaterTankThatHasNotEmittedYet

  const hasNoDataForHorizon = isDevice
    ? false
    : computeHasNoDataForHorizon({ metric: focusedMetric, horizonId: focusedHorizon, summary })

  // Arvalis — plot only (always false for stations/devices)
  const isArvalisPlot = isArvalisPlotSummary(summary)
  const shouldConfigureArvalis = isArvalisPlot && !summary.plotIsSetupForCurrentSeason
  const arvalisWBComputing = isArvalisPlot && summary.waterBalanceStatus === 'running'
  const arvalisWBError = isArvalisPlot && summary.waterBalanceStatus === 'error'
  const shouldConfigureMetric = isPlot
    ? (summary.metadata.notConfigured?.includes(focusedMetric) ?? false)
    : false

  const { isRestricted, reason } = useIsAppAccessRestricted({ isVWS: isVirtual })

  const CardIcon = isPlot ? (
    <Icons.PlotsFilled $size='lg' />
  ) : isVirtual ? (
    <Icons.SingleVirtualSensorFilled $size='lg' />
  ) : (
    <Icons.SingleSensorFilled $size='lg' />
  )

  // Footer
  const shouldHideFooter =
    hasNoDataForHorizon ||
    isFrost(focusedMetric) ||
    ((isStation || isDevice) && isSoilSensorMetric(focusedMetric))

  const showConfigureLink =
    !shouldHideFooter &&
    isPlot &&
    !hasNotEmitted &&
    (shouldConfigureArvalis || shouldConfigureMetric)

  return (
    <StyledCard $isSelected={isSelected} onPress={onPress}>
      <Flex $flexDirection='column' $height='100%'>
        <SummaryCardHeader
          name={name}
          reverseGeocoding={reverseGeocoding}
          Icon={CardIcon}
          onFocusPress={focusOnMap}
          FavoriteSlot={
            !isDevice && !isNil(summariesQueryCacheKey) && !on_nav_favorites ? (
              <FavoriteButton summary={summary} summariesQueryCacheKey={summariesQueryCacheKey} />
            ) : null
          }
        />

        {!isNil(focusedMetric) ? (
          <SummaryCardContent
            summary={summary}
            focusedMetric={focusedMetric}
            focusedHorizon={focusedHorizon}
            availableHorizonOptions={availableHorizonOptions}
            onHorizonChange={onHorizonChange}
            isDiscrete={isDiscrete}
            isRestricted={isRestricted}
            reason={reason}
            isVirtualDevice={isVirtual}
            providerName={providerName}
            timezone={timezone}
            hasNotEmitted={hasNotEmitted}
            hasNoDataForHorizon={hasNoDataForHorizon}
            shouldConfigureArvalis={shouldConfigureArvalis}
            shouldConfigureMetric={shouldConfigureMetric}
            arvalisWBComputing={arvalisWBComputing}
            arvalisWBError={arvalisWBError}
          />
        ) : null}

        <SummaryCardFooter
          shouldHide={shouldHideFooter}
          showConfigureLink={showConfigureLink}
          shouldConfigureMetric={shouldConfigureMetric}
          timestamp={lastUpdate}
          timezone={timezone}
          isVirtualMetric={marker_redesign ? undefined : isVirtual}
          warningThreshold={
            marker_redesign ? lastUpdateThresholds.warningThreshold : warningThreshold
          }
          dangerThreshold={lastUpdateThresholds.dangerThreshold}
          providerName={providerName}
        />
      </Flex>
    </StyledCard>
  )
}

export default memo(SummaryCard)
